import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Bell, CheckCircle2, AlertTriangle, FileText, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

interface ActivityItem {
  id: string;
  type: "submission" | "finalized" | "overdue" | "all_in";
  title: string;
  meta: string;
  href: string;
  timestamp: Date;
  icon: typeof Bell;
  iconClass: string;
}

const LAST_SEEN_KEY = "upbrief_notifications_last_seen";

/**
 * Header bell showing recent activity (submissions received,
 * reports finalized, overdue alerts).
 */
export const NotificationBell = () => {
  const { data: activity, isLoading } = useQuery({
    queryKey: ["notification_activity"],
    queryFn: async (): Promise<ActivityItem[]> => {
      const items: ActivityItem[] = [];

      // Recent submissions (last 7 days)
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: subs } = await supabase
        .from("submission_requests")
        .select("id, submitted_at, cycle_id, template_sections(name, assignee_name), report_cycles(title)")
        .eq("status", "submitted")
        .gte("submitted_at", weekAgo)
        .order("submitted_at", { ascending: false })
        .limit(8);

      (subs || []).forEach((s: any) => {
        if (!s.submitted_at) return;
        items.push({
          id: `sub-${s.id}`,
          type: "submission",
          title: `${s.template_sections?.assignee_name || "A contributor"} submitted`,
          meta: `${s.report_cycles?.title || "Cycle"} · ${s.template_sections?.name || ""}`,
          href: `/cycles/${s.cycle_id}`,
          timestamp: new Date(s.submitted_at),
          icon: CheckCircle2,
          iconClass: "text-success",
        });
      });

      // Recently finalized reports
      const { data: finalized } = await supabase
        .from("ai_cycle_summaries")
        .select("id, cycle_id, approved_at, report_cycles(title)")
        .eq("status", "approved")
        .gte("approved_at", weekAgo)
        .order("approved_at", { ascending: false })
        .limit(5);

      (finalized || []).forEach((f: any) => {
        if (!f.approved_at) return;
        items.push({
          id: `final-${f.id}`,
          type: "finalized",
          title: "Report finalized",
          meta: f.report_cycles?.title || "Untitled",
          href: `/reports/${f.cycle_id}`,
          timestamp: new Date(f.approved_at),
          icon: FileText,
          iconClass: "text-primary",
        });
      });

      // Overdue cycles
      const now = new Date().toISOString();
      const { data: overdue } = await supabase
        .from("report_cycles")
        .select("id, title, due_date")
        .eq("status", "active")
        .lt("due_date", now)
        .order("due_date", { ascending: false })
        .limit(5);

      (overdue || []).forEach((c: any) => {
        items.push({
          id: `over-${c.id}`,
          type: "overdue",
          title: "Cycle overdue",
          meta: `${c.title} · was due ${format(new Date(c.due_date), "MMM d")}`,
          href: `/cycles/${c.id}`,
          timestamp: new Date(c.due_date),
          icon: AlertTriangle,
          iconClass: "text-warning",
        });
      });

      // Sort newest first, cap at 15
      return items
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, 15);
    },
    refetchInterval: 60_000,
  });

  const lastSeen = typeof window !== "undefined"
    ? Number(localStorage.getItem(LAST_SEEN_KEY) || 0)
    : 0;
  const unread = (activity || []).filter((a) => a.timestamp.getTime() > lastSeen).length;

  const markRead = () => {
    localStorage.setItem(LAST_SEEN_KEY, String(Date.now()));
  };

  return (
    <Popover onOpenChange={(open) => { if (open) markRead(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 flex items-center justify-center text-[10px] bg-destructive hover:bg-destructive text-destructive-foreground border-0"
            >
              {unread > 9 ? "9+" : unread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="font-semibold text-sm">Recent Activity</h3>
          <p className="text-xs text-muted-foreground">Last 7 days</p>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-3 space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : !activity || activity.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <Clock className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No recent activity</p>
            </div>
          ) : (
            <div className="py-1">
              {activity.map((item) => (
                <Link
                  key={item.id}
                  to={item.href}
                  className="flex items-start gap-3 px-4 py-2.5 hover:bg-muted transition-colors"
                >
                  <item.icon className={`w-4 h-4 mt-0.5 shrink-0 ${item.iconClass}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">
                      {item.title}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {item.meta}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {formatDistanceToNow(item.timestamp, { addSuffix: true })}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
